import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, Loader2, X } from "lucide-react";
import { api, errorMessage, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";

export const ImageUpload = ({ value, onChange, shape = "circle", testId = "image-upload", endpoint = "/admin/upload", allowUrl = true, hint = true, fit = "cover" }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [showUrl, setShowUrl] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be under 8 MB");
    const form = new FormData();
    form.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post(endpoint, form, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || file.size)) * 100)) });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setProgress(null); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); };
  const radius = shape === "circle" ? "rounded-full" : "rounded-xl";

  return (
    <div className="flex flex-col items-center gap-3" data-testid={testId}>
      <button type="button" onClick={() => inputRef.current?.click()} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
        className={`group relative h-32 w-32 ${radius} overflow-hidden ring-2 transition-colors ${dragging ? "ring-sky-400 bg-sky-400/10" : "ring-white/10 bg-[#053671] hover:ring-sky-400/60"}`} data-testid={`${testId}-dropzone`} aria-label="Upload photo">
        {value ? <img src={imgUrl(value)} alt="" className={`h-full w-full ${fit === "contain" ? "object-contain p-2 bg-white" : "object-cover"}`} data-testid={`${testId}-preview`} /> : <span className="absolute inset-0 flex items-center justify-center text-sky-200"><Upload size={28} /></span>}
        <span className={`absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[#070F1E]/80 text-xs font-semibold text-white transition-opacity ${value ? "opacity-0 group-hover:opacity-100" : "opacity-0"}`}><Upload size={16} /> Replace</span>
        {progress !== null && (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-[#070F1E]/85 text-xs font-mono text-sky-300" data-testid={`${testId}-progress`}><Loader2 size={18} className="animate-spin" />{progress}%</span>
        )}
      </button>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid={`${testId}-input`} />
      <div className="flex items-center gap-3 text-xs">
        <button type="button" onClick={() => inputRef.current?.click()} className="font-semibold text-sky-400 hover:text-sky-300" data-testid={`${testId}-choose`}>{value ? "Change photo" : "Upload photo"}</button>
        {allowUrl && <><span className="text-slate-600">·</span>
        <button type="button" onClick={() => setShowUrl((s) => !s)} className="inline-flex items-center gap-1 text-slate-400 hover:text-white" data-testid={`${testId}-toggle-url`}><Link2 size={12} /> Use URL</button></>}
        {value && <><span className="text-slate-600">·</span><button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1 text-slate-400 hover:text-rose-400" data-testid={`${testId}-remove`}><X size={12} /> Remove</button></>}
      </div>
      {hint && <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Drag & drop · JPG, PNG, WebP · up to 8 MB</p>}
      {allowUrl && showUrl && <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…/photo.jpg" className="bg-white/5 border-white/10 w-full" data-testid={`${testId}-url-input`} />}
    </div>
  );
};
